import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, Pencil, Trash2, Copy, ExternalLink, MessageCircle, ListChecks, Search, Printer, Eye } from "lucide-react";
import { toast } from "sonner";
import { buildShortlistShareText, whatsappLink } from "../lib/brand";
import AdminShortlistFlow from "../shared/components/AdminShortlistFlow";
// v2.80 — Refresh on cross-session shortlist + property mutations.
import { useDataSync } from "../lib/dataSync";

const emptyShortlist = () => ({ name: "", client_name: "", notes: "", property_ids: [] });

// v2.71.55 — Legacy `<ShortlistDialog>` removed. AdminShortlistFlow now provides
// the same workflow but as a full-screen drawer with horizontal property cards,
// images, search, and multiselect — matching the team-member shortlist UX.

export default function ShortlistsTab({ allProperties, mode = "admin" }) {
  const [shortlists, setShortlists] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [props, setProps] = useState(allProperties || []);

  const load = async (q = "") => {
    setLoading(true);
    try {
      const r = await api.get("/admin/shortlists", { params: q ? { q } : {} });
      setShortlists(r.data || []);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to load shortlists"));
    }
    setLoading(false);
  };

  // v2.71.13 — When mounted from TeamDashboard (no allProperties prop), auto-fetch.
  // v2.71.55 — Also re-sync when allProperties prop changes from parent (admin page
  //            often loads listings async; first mount may have empty array).
  useEffect(() => {
    load();
    if (!allProperties) {
      api.get("/properties", { params: { limit: 200 } })
        .then((r) => setProps(r.data?.results || r.data || []))
        .catch(() => setProps([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Array.isArray(allProperties)) setProps(allProperties);
  }, [allProperties]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(query), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // v2.80 — Refresh on any shortlist or property mutation (so changes
  // from other admins or auto-update on delete reflect instantly).
  useDataSync(["shortlists", "properties"], () => { load(query); });

  const del = async (id) => {
    if (!window.confirm("Delete this shortlist?")) return;
    try {
      await api.delete(`/admin/shortlists/${id}`);
      toast.success("Deleted");
      load(query);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Delete failed"));
    }
  };

  const copyLink = (slug) => {
    const link = `${window.location.origin}/shortlist/${slug}`;
    navigator.clipboard.writeText(link);
    toast.success("Public link copied");
  };

  const shareWA = (sl) => {
    const link = `${window.location.origin}/shortlist/${sl.slug}`;
    const count = sl.property_count ?? (sl.property_ids?.length || 0);
    const text = buildShortlistShareText(sl, count, link);
    window.open(whatsappLink(text), "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, client, notes…"
            className="pl-8 h-9 text-[13px]"
            data-testid="shortlist-search"
          />
        </div>
        <Button
          onClick={() => { setEditing(null); setShowDialog(true); }}
          className="btn-brand rounded-sm"
          data-testid="add-shortlist-btn"
        >
          <Plus size={14} className="mr-1.5" /> New Shortlist
        </Button>
      </div>
      <div className="space-y-3">
        {loading && shortlists.length === 0 && (
          <div className="text-center py-6 text-[#A1A1AA] text-[12px]">Loading…</div>
        )}
        {!loading && shortlists.length === 0 && (
          <div className="text-center py-10 text-[#A1A1AA] bg-white border border-dashed border-[#E4E4E7] rounded-sm" data-testid="shortlists-empty">
            <ListChecks size={28} className="mx-auto mb-2 text-[#D4D4D8]" />
            {query
              ? <>No shortlists match <b>"{query}"</b>.</>
              : (mode === "team"
                ? "No shortlists yet. Create one to share curated property packs with your clients via WhatsApp or print."
                : "No shortlists yet. Create one to share curated property packs with clients via WhatsApp or print.")
            }
          </div>
        )}
        {shortlists.map((sl) => {
          const count = sl.property_count ?? (sl.property_ids?.length || 0);
          return (
            <div key={sl.id} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`shortlist-row-${sl.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <h4 className="font-heading font-semibold text-[15px] tracking-tight">{sl.name}</h4>
                  <div className="text-[12px] text-[#52525B] mt-0.5">
                    {sl.client_name && <span>For <b>{sl.client_name}</b> · </span>}
                    <b className="text-[#0055FF] font-mono-data">{count}</b> properties
                    {sl.view_count > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[#15803D] font-semibold" data-testid={`view-count-${sl.id}`}>
                        <Eye size={11} /> {sl.view_count} {sl.view_count === 1 ? "view" : "views"}
                      </span>
                    )}
                  </div>
                  {sl.notes && <div className="text-[12px] text-[#52525B] italic mt-1">"{sl.notes}"</div>}
                  {mode === "admin" && sl.created_by_name && (
                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1">
                      by {sl.created_by_name}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => copyLink(sl.slug)} data-testid={`copy-link-${sl.id}`}>
                    <Copy size={12} className="mr-1.5" /> Copy link
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white" onClick={() => shareWA(sl)} data-testid={`wa-${sl.id}`}>
                    <MessageCircle size={12} className="mr-1.5" /> WhatsApp
                  </Button>
                  <a href={`/shortlist/${sl.slug}/print`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="rounded-sm" data-testid={`print-${sl.id}`}>
                      <Printer size={12} className="mr-1.5" /> Print
                    </Button>
                  </a>
                  {/* v2.75 — same-tab nav. Print preview stays new-tab (PDF use case). */}
                  <Link to={`/shortlist/${sl.slug}`}>
                    <Button size="sm" variant="outline" className="rounded-sm" data-testid={`view-${sl.id}`}>
                      <ExternalLink size={12} className="mr-1.5" /> View
                    </Button>
                  </Link>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(sl); setShowDialog(true); }} data-testid={`edit-shortlist-${sl.id}`}>
                    <Pencil size={13} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => del(sl.id)} data-testid={`delete-shortlist-${sl.id}`}>
                    <Trash2 size={13} className="text-[#DC2626]" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AdminShortlistFlow
        open={showDialog}
        onOpenChange={(v) => { setShowDialog(v); if (!v) setEditing(null); }}
        mode={editing ? "edit" : "create"}
        editingId={editing?.id || null}
        allProperties={props}
        onSaved={() => load(query)}
      />
    </>
  );
}
